// The Sharing audit's reading of the trail (ARCHITECTURE.md §14). A device
// reports each share through its outbox as `collection.shared`, its end as
// `collection.share_ended`, a collection it took in as
// `collection.received`, and one it refused as another organization's as
// `collection.transfer_refused`, each with a flat detail. The console adds
// `collection.share_refused` to the sender's organization, and
// `collection.transferred` to both organizations of a completed transfer
// between them, each naming the other side from its own event. Every event of a
// share names its `share_id` (the sender's), which is how the page finds
// the other half whatever page it is on. Pure, so it is tested apart from
// the page.

import type { AuditEvent } from '@motionsafe/ms-console-api/admin/v1/audit_pb.js';

/** What the table lists: each share, each collection received, and each transfer refused, on either side. */
export const SHARING_ACTIONS = ['collection.shared', 'collection.received', 'collection.transfer_refused', 'collection.share_refused'];
/** How a share ended, joined to its share by `share_id`. */
export const SHARE_ENDED = 'collection.share_ended';
/** The console's line in each organization of a completed transfer between organizations. */
export const TRANSFERRED = 'collection.transferred';
/** Everything that can belong to a share, looked up by `share_id` for the page in view. */
export const COUNTERPART_ACTIONS = [...SHARING_ACTIONS, SHARE_ENDED, TRANSFERRED];
/** How many shares one lookup may name; the service takes at most 100. */
export const MAX_SHARE_IDS = 100;

/** The Event column's words for each action. */
const EVENT_WORDS: Record<string, string> = {
  'collection.shared': 'Shared',
  'collection.received': 'Received',
  'collection.transfer_refused': 'Refused: from another organization',
  'collection.share_refused': 'Refused by another organization',
};

export const eventWord = (action: string): string => EVENT_WORDS[action] ?? action;

/** A detail field as text, or '' when the event did not record it as text. */
export function detailText(e: AuditEvent, key: string): string {
  const v = e.detail?.[key];
  return typeof v === 'string' ? v : '';
}

/** A detail field as a number, or null. */
export function detailNumber(e: AuditEvent, key: string): number | null {
  const v = e.detail?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Whether the share was decrypted; only `true` counts. */
export const isDecrypted = (e: AuditEvent): boolean => e.detail?.['decrypted'] === true;

/** The transport as people say it; a value this page does not know is shown as recorded. */
export function transportName(transport: string): string {
  if (transport === 'usb_mtp') return 'USB';
  if (transport === 'wifi_direct') return 'Wi-Fi Direct';
  return transport;
}

/** How long after the device's clock the console may record an event before the page says so. */
export const LATE_AFTER_S = 10 * 60;

/**
 * When the console recorded an event well after the device says it happened
 * (a device that was offline), how much later: "Recorded 3 h later". Null
 * when it arrived within ten minutes, or the clocks put it before.
 */
export function recordedLater(e: Pick<AuditEvent, 'occurredAt' | 'receivedAt'>): string | null {
  if (!e.occurredAt || !e.receivedAt) return null;
  const s = Number(e.receivedAt.seconds - e.occurredAt.seconds);
  if (s <= LATE_AFTER_S) return null;
  const mins = Math.round(s / 60);
  if (mins < 60) return `Recorded ${mins} min later`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `Recorded ${hours} h later`;
  return `Recorded ${Math.floor(hours / 24)} days later`;
}

/** The share IDs a page names, each once, at most `MAX_SHARE_IDS`. */
export function shareIdsOf(rows: readonly AuditEvent[]): string[] {
  return [...new Set(rows.map((e) => detailText(e, 'share_id')).filter(Boolean))].slice(0, MAX_SHARE_IDS);
}

/** Every event recorded for one share, one of each kind. */
export interface Halves {
  shared?: AuditEvent;
  received?: AuditEvent;
  ended?: AuditEvent;
  transferRefused?: AuditEvent;
  shareRefused?: AuditEvent;
  /** One per organization of a transfer between organizations; staff may see both. */
  transferred?: AuditEvent[];
}

const SLOT: Record<string, Exclude<keyof Halves, 'transferred'>> = {
  'collection.shared': 'shared',
  'collection.received': 'received',
  [SHARE_ENDED]: 'ended',
  'collection.transfer_refused': 'transferRefused',
  'collection.share_refused': 'shareRefused',
};

/** Each share's events by its `share_id`; where one kind was reported more than once, the first the device recorded. */
export function halvesByShare(events: readonly AuditEvent[]): Map<string, Halves> {
  const byShare = new Map<string, Halves>();
  for (const e of events) {
    const id = detailText(e, 'share_id');
    if (id && e.action === TRANSFERRED) {
      const h = byShare.get(id) ?? {};
      h.transferred = [...(h.transferred ?? []), e];
      byShare.set(id, h);
      continue;
    }
    const slot = SLOT[e.action];
    if (!id || !slot) continue;
    const h = byShare.get(id) ?? {};
    const had = h[slot];
    if (!had || (e.occurredAt?.seconds ?? 0n) < (had.occurredAt?.seconds ?? 0n)) h[slot] = e;
    byShare.set(id, h);
  }
  return byShare;
}

/** The console's line for this row's transfer in the row's own organization. */
const transferredHere = (e: AuditEvent, h: Halves | undefined) => h?.transferred?.find((t) => t.orgId === e.orgId);

/** What a row is linked to on the record, said under its event: the other half of its share. */
export function linkCaption(e: AuditEvent, h: Halves | undefined): string | null {
  if (!h) return null;
  const other = (x: AuditEvent | undefined) => !!x && x.id !== e.id;
  if (e.action === 'collection.shared') {
    if (other(h.received) || transferredHere(e, h)) return 'Linked to its receipt';
    if (other(h.transferRefused) || other(h.shareRefused)) return 'Linked to its refusal';
    return null;
  }
  return other(h.shared) || (e.action === 'collection.received' && transferredHere(e, h)) ? 'Linked to its send' : null;
}

/**
 * Who was on the other end of a Wi-Fi Direct transfer. `confirmed` when it
 * comes from the console's own record (the other half's event, or the
 * console's refusal line); otherwise it is what the other device said about
 * itself, until the other half syncs.
 */
export interface Peer {
  direction: 'From' | 'To';
  name: string;
  org: string;
  device: string;
  confirmed: boolean;
  /** The other end is another organization. */
  crossOrg: boolean;
}

const named = (p: Peer): Peer | null => (p.name || p.org || p.device ? p : null);

/** Whether the device that reported this event said its transfer crossed organizations. */
const markedCrossOrg = (e: AuditEvent): boolean => e.detail?.['cross_org'] === true || e.action === 'collection.transfer_refused';

/** The other end as this event's own device was told it. */
export function peerOf(e: AuditEvent, end: AuditEvent | undefined): Peer | null {
  if (e.action === 'collection.received' || e.action === 'collection.transfer_refused') {
    return named({ direction: 'From', name: detailText(e, 'from_operator_name'), org: detailText(e, 'from_org'), device: detailText(e, 'from_device'), confirmed: false, crossOrg: markedCrossOrg(e) });
  }
  if (e.action !== 'collection.shared' || detailText(e, 'transport') !== 'wifi_direct' || !end || detailText(end, 'reason') !== 'completed') return null;
  return named({ direction: 'To', name: detailText(end, 'to_operator_name'), org: detailText(end, 'to_org'), device: detailText(end, 'to_device'), confirmed: false, crossOrg: markedCrossOrg(e) });
}

/** The receiver named by the console's own refusal line in the sender's organization. */
function refusedPeer(e: AuditEvent): Peer | null {
  return named({ direction: 'To', name: '', org: detailText(e, 'receiving_org'), device: detailText(e, 'receiving_device'), confirmed: true, crossOrg: true });
}

/** The other side named by the console's line for a transfer between organizations. */
function transferredPeer(t: AuditEvent): Peer | null {
  return named({ direction: detailText(t, 'direction') === 'in' ? 'From' : 'To', name: detailText(t, 'other_operator_name'), org: detailText(t, 'other_org_name') || detailText(t, 'other_org'), device: detailText(t, 'other_device'), confirmed: true, crossOrg: true });
}

/** "From J. Rivera, county-so"; the device when neither name nor organization was given; marked when it is another organization. */
export function peerText(p: Peer): string {
  const who = [p.name, p.org].filter(Boolean).join(', ');
  return `${p.direction} ${who || p.device}${p.crossOrg ? ' (another organization)' : ''}`;
}

/**
 * The other end as its own event records it: the organization it was filed
 * under (named by `orgName`), its device, and its operator when one signed
 * in on that device. What the other device claimed fills in the rest.
 */
export function confirmedPeer(direction: Peer['direction'], other: AuditEvent, orgName: string, claimed: Peer | null, crossOrg = false): Peer {
  const operator = other.actor?.type === 'user' ? other.actor.name : '';
  return { direction, name: operator || claimed?.name || '', org: orgName || claimed?.org || '', device: other.deviceId || claimed?.device || '', confirmed: true, crossOrg };
}

/**
 * Who was on the other end of this row's transfer, from the other half's own
 * event when it is on record, from the console's refusal line, or from what
 * the other device said. Null for USB and a send not known to have reached
 * anyone. `orgName` names an organization by its ID, '' when it cannot.
 */
export function peerFor(e: AuditEvent, h: Halves | undefined, orgName: (orgId: string) => string): Peer | null {
  const other = (x: AuditEvent | undefined) => (x && x.id !== e.id ? x : undefined);
  const confirm = (direction: Peer['direction'], x: AuditEvent, claimed: Peer | null) => confirmedPeer(direction, x, orgName(x.orgId), claimed, x.orgId !== e.orgId);
  const line = transferredHere(e, h);
  if (e.action === 'collection.received' || e.action === 'collection.transfer_refused') {
    const send = other(h?.shared);
    if (send) return confirm('From', send, peerOf(e, undefined));
    return (line && transferredPeer(line)) ?? peerOf(e, undefined);
  }
  if (e.action === 'collection.share_refused') {
    const refusal = other(h?.transferRefused);
    return refusal ? confirm('To', refusal, refusedPeer(e)) : refusedPeer(e);
  }
  if (e.action !== 'collection.shared' || detailText(e, 'transport') !== 'wifi_direct') return null;
  const receiver = other(h?.received) ?? other(h?.transferRefused);
  if (receiver) return confirm('To', receiver, peerOf(e, h?.ended));
  if (line) return transferredPeer(line);
  if (h?.shareRefused) return refusedPeer(h.shareRefused);
  return peerOf(e, h?.ended);
}
